import { useTranslations } from 'next-intl';
import { useState } from 'react';
import ShippingInfoBranchCard from '@/components/ui/cards/shipping-info-branch-card';
import ShippingInfoBranchCardSkeleton from '@/components/ui/skeletons/Shipping-Info-Branch-Card-Skeleton';
import { Button } from '@/components/ui/buttons';
import { useSession } from 'next-auth/react';
import { getCookie } from 'cookies-next';
import { setShippingAddress } from '@/services/checkout';
import { toast } from '@/utils/toast';
import useShippingInfoStore from '@/store/shipping-info.store';

const UnavailableItemsBranches = ({
  handleOnClose,
  setSelectedBranch,
  selectedBranch,
  availableBranches = [],
  unavailableItems = [],
  isLoadingBranches = false,
}) => {
  const t = useTranslations('modals');
  const { data: session } = useSession();
  const [selectedBranchData, setSelectedBranchData] = useState(selectedBranch);
  const [isUpdating, setIsUpdating] = useState(false);
  const shippingAddress = useShippingInfoStore(
    (state) => state.shippingAddress
  );

  const handleBranchSelect = (branch) => {
    const selectedBranchObj = {
      firstname: branch?.name,
      street: [branch?.address],
      telephone: branch?.phone || session?.user?.mobilenumber,
      region_id: branch?.region_id || shippingAddress?.region_id,
      country_id: branch?.country_id || 'SA',
      id: branch?.location_id || branch?.id,
      postcode: branch?.zip || branch?.postcode,
      lat: branch?.latitude || branch?.lat,
      lng: branch?.longitude || branch?.lng,
      city: branch?.city || shippingAddress?.city,
      email: session?.user?.email,
      city_id: branch?.city_id || shippingAddress?.city_id,
      source_code: branch?.source_code,
      source_region_id: branch?.source_region_id || branch?.region_id,
      source_city_id: branch?.source_city_id || branch?.city_id,
    };
    setSelectedBranchData(selectedBranchObj);
  };

  const handleConfirmBranch = async () => {
    if (!selectedBranchData?.id) return;

    const mage = getCookie('mage');
    const quote = getCookie('quote');

    if (!mage || !quote) {
      toast.error(t('error_updating_branch') || 'Error updating branch');
      return;
    }

    setIsUpdating(true);
    try {
      const updatedAddress = { ...selectedBranchData };
      console.log('updatedAddress', updatedAddress);
      updatedAddress.telephone =
        session?.user?.mobilenumber || updatedAddress.telephone;
      updatedAddress.email = session?.user?.email || updatedAddress.email;
      updatedAddress.firstname =
        session?.user?.firstname || updatedAddress.firstname;
      updatedAddress.country_id = 'SA';
      updatedAddress.region = updatedAddress.region_id;
      // ensure postcode/city fallback
      updatedAddress.postcode =
        updatedAddress.postcode || shippingAddress?.postcode || '00000';
      updatedAddress.city =
        updatedAddress.city || shippingAddress?.city || 'Riyadh';

      // Use source_code or location_id for store pickup
      const storeId =
        selectedBranchData?.location_id ||
        selectedBranchData?.id ||
        selectedBranchData?.source_code;

      const res = await setShippingAddress(
        mage,
        quote,
        updatedAddress,
        'amstorepickup',
        typeof storeId === 'string' ? Number(storeId) : storeId
      );

      if (res?.status === 200 && !res?.error) {
        setSelectedBranch(selectedBranchData);
        handleOnClose();
      } else {
        toast.error(
          res?.data?.error ||
            res?.error?.error?.message ||
            t('error_updating_branch') ||
            'Error updating branch'
        );
      }
    } catch (error) {
      console.error('Error updating branch:', error);
      toast.error(t('error_updating_branch') || 'Error updating branch');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseWithoutChange = () => {
    handleOnClose();
  };

  return (
    <div className='space-y-4 px-[22px] pt-3.5 pb-[19px]'>
      <div className='border-b border-b-gray-200 pb-2'>
        <h2 className='text-lg font-semibold text-red-600'>
          {t('unavailable_items_title')}
        </h2>
        <p className='mt-2 text-sm text-gray-700'>
          {t('unavailable_items_description')}
        </p>
        {unavailableItems.length > 0 && (
          <p className='mt-1 text-xs text-gray-600'>
            {t('unavailable_items_count', { count: unavailableItems.length })}
          </p>
        )}
      </div>

      <div>
        <h3 className='mb-3 text-base font-semibold'>
          {t('available_branches_title')}
        </h3>
        {isLoadingBranches ? (
          Array.from({ length: 3 }).map((_, i) => (
            <ShippingInfoBranchCardSkeleton key={'branch-skeleton-' + i} />
          ))
        ) : (
          <div className='blue-scroll max-h-[35vh] overflow-y-auto px-1 max-sm:px-0'>
            {availableBranches?.length > 0 ? (
              availableBranches?.map((branch, i) => (
                <ShippingInfoBranchCard
                  key={'branch-card-' + i}
                  branch={{
                    ...branch,
                    id: branch?.location_id || branch?.id,
                    name: branch?.name,
                    address: branch?.address,
                    lat: branch?.latitude || branch?.lat,
                    lng: branch?.longitude || branch?.lng,
                    available: true,
                  }}
                  t={t}
                  handleBranchSelect={handleBranchSelect}
                  selectedBranch={selectedBranchData}
                />
              ))
            ) : (
              <div className='py-8 text-center text-gray-500'>
                {t('no_branches_found') || 'No branches found'}
              </div>
            )}
          </div>
        )}
      </div>

      <div className='border-t border-t-[#DDDDDD] max-sm:px-1.75'>
        <div className='flex flex-col gap-2'>
          {selectedBranchData?.id && (
            <Button
              onClick={handleConfirmBranch}
              disabled={isUpdating}
              className='bg-primary mx-auto mt-[15px] flex h-[45px] w-full items-center justify-center rounded-[5px] text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 max-sm:w-full'
            >
              {isUpdating ? (
                <span className='mr-2 size-4 animate-spin rounded-full border-t border-r border-white' />
              ) : null}
              {t('confirm_branch')}
            </Button>
          )}
          <Button
            onClick={handleCloseWithoutChange}
            variant='outline'
            className='mx-auto flex h-[45px] w-full items-center justify-center rounded-[5px] border border-gray-300 bg-white text-[15px] font-bold text-gray-700! hover:bg-gray-50 max-sm:w-full'
          >
            {t('continue_without_change') || 'Continue without changing branch'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UnavailableItemsBranches;
